import React, { useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import { readStoredUser } from "../../../utils/sessionUser";
import { applyOcrAiProposal, buildOcrAiCurrentReview, ocrAiComparisonRows, ocrAiDraftSignature, validateOcrAiProposal } from "../../utils/ocrAiReview";
import { prepareSavedOcrAiProposal } from "../../utils/ocrAiReviewHistory";
import OcrAiReviewComments from "./OcrAiReviewComments";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import "./OcrAiReviewPanel.css";

const errors = {
  OCR_AI_NOT_CONFIGURED: "AI review is not available yet. You can continue reviewing manually.",
  OCR_AI_CONTEXT_TOO_LARGE: "This document has too many details for AI review. Continue reviewing manually.",
  OCR_AI_DOCUMENT_TOO_LARGE: "This document is too large for AI review. Use a smaller document or review it manually.",
  OCR_AI_TOO_MANY_PAGES: "This document has too many pages for AI review. Continue reviewing manually.",
  OCR_AI_INVALID_DOCUMENT: "The original document could not be read. Check the original and review it manually.",
  OCR_AI_DOCUMENT_INVALID: "The original document could not be read. Check the original and review it manually.",
  OCR_AI_REVIEW_BUSY: "A review is already running for this document. Try again shortly.",
  OCR_AI_REVIEW_STALE: "The document changed during review. Reopen it before trying again.",
  OCR_AI_ORIGINAL_MISSING: "The original document is no longer available. You can continue reviewing manually.",
  OCR_AI_IMPORT_NOT_REVIEWABLE: "This document cannot be reviewed in its current state. Refresh the inbox.",
  OCR_AI_REVIEW_REFUSED: "The document could not be reviewed with AI. You can continue reviewing manually.",
  OCR_AI_REVIEW_TIMEOUT: "AI review took too long. Try again or continue reviewing manually.",
};
const STALE = "The draft or business unit changed. Review it again before applying a proposal.";

export default function OcrAiReviewPanel({ importData, businessUnitId, requestClient = axios, review, items = [], suppliers = [], itemsLoaded = true, itemsSupplierId = review.supplierId, disabled = false, onApply, onBusyChange }) {
  const { t, locale } = useReceivingPopupUi();
  const [state, setState] = useState({ pending: false, result: null, error: "" });
  const [history, setHistory] = useState({ open: false, pending: false, loaded: false, reviews: [], error: "" });
  const [ownerId] = useState(() => readStoredUser()?.id);
  const historyRequest = useRef({ id: 0, controller: null });
  const request = useRef({ id: 0, pending: false, controller: null });
  const live = useRef(null);
  const busyCallback = useRef(onBusyChange);
  busyCallback.current = onBusyChange;
  const signature = ocrAiDraftSignature(review, items);
  const originalIdentity = JSON.stringify([importData.s3_bucket, importData.s3_key, importData.file_hash]);
  const documentAvailable = Boolean(importData.s3_key && !importData.created_delivery_note_id &&
    !["processing", "created", "confirmed", "discarded", "duplicate"].includes(importData.status));
  live.current = { importId: importData.id, businessUnitId, signature, review, items, suppliers, itemsLoaded, itemsSupplierId, documentAvailable, originalIdentity };
  const currentScope = () => {
    const user = readStoredUser();
    return String(user?.id || "") === String(ownerId || "") &&
      String(user?.active_business_unit_id || "") === String(live.current.businessUnitId || "");
  };

  useEffect(() => {
    setState({ pending: false, result: null, error: "" });
    setHistory({ open: false, pending: false, loaded: false, reviews: [], error: "" });
    const invalidate = () => {
      const user = readStoredUser();
      if (String(user?.id || "") === String(ownerId || "") && String(user?.active_business_unit_id || "") === String(businessUnitId || "")) return;
      request.current.controller?.abort();
      request.current = { id: request.current.id + 1, pending: false, controller: null };
      busyCallback.current?.(false);
      setState({ pending: false, result: null, error: STALE });
      historyRequest.current.controller?.abort();
      historyRequest.current.id += 1;
      setHistory({ open: false, pending: false, loaded: false, reviews: [], error: "" });
    };
    window.addEventListener("storage", invalidate);
    window.addEventListener("palantir-session-updated", invalidate);
    return () => {
      request.current.controller?.abort();
      request.current = { id: request.current.id + 1, pending: false, controller: null };
      historyRequest.current.controller?.abort();
      historyRequest.current.id += 1;
      busyCallback.current?.(false);
      window.removeEventListener("storage", invalidate);
      window.removeEventListener("palantir-session-updated", invalidate);
    };
  }, [importData.id, businessUnitId, originalIdentity, ownerId]);

  const canRequest = Boolean(documentAvailable && businessUnitId);
  const loadHistory = async () => {
    if (!currentScope()) return;
    historyRequest.current.controller?.abort();
    const controller = new AbortController();
    const id = historyRequest.current.id + 1;
    historyRequest.current = { id, controller };
    const snapshot = { ...live.current };
    const knownReviewIds = new Set(history.reviews.map((entry) => entry.review_id));
    setHistory((previous) => ({ ...previous, open: true, pending: true, error: "" }));
    try {
      const { data } = await requestClient.get(`/api/purchasing/ocr-imports/${snapshot.importId}/ai-reviews`, {
        signal: controller.signal, pilcoreExpectedUserId: ownerId, headers: { "X-Pilcore-Business-Unit-Id": snapshot.businessUnitId },
      });
      if (controller.signal.aborted || id !== historyRequest.current.id || !currentScope() || snapshot.importId !== live.current.importId || snapshot.businessUnitId !== live.current.businessUnitId || snapshot.originalIdentity !== live.current.originalIdentity) return;
      if (!Array.isArray(data?.reviews)) throw new Error("Invalid review history");
      setHistory((previous) => ({ ...previous, pending: false, loaded: true, reviews: [
        ...previous.reviews.filter((entry) => !knownReviewIds.has(entry.review_id) && !data.reviews.some((saved) => saved.review_id === entry.review_id)),
        ...data.reviews,
      ], error: "" }));
    } catch (_error) {
      if (controller.signal.aborted || id !== historyRequest.current.id || !currentScope()) return;
      setHistory((previous) => ({ ...previous, pending: false, error: "Could not load saved AI reviews. Try again." }));
    }
  };
  const previewSaved = (entry) => {
    if (disabled || request.current.pending || !currentScope()) return;
    try {
      const snapshot = live.current;
      const proposal = prepareSavedOcrAiProposal(entry, snapshot.review, snapshot.items);
      setState({ pending: false, error: "", result: { proposal, baseline: buildOcrAiCurrentReview(snapshot.review, snapshot.items),
        signature: snapshot.signature, saved: true, canApply: entry.can_reapply === true, reviewId: entry.review_id } });
    } catch (_error) {
      setState({ pending: false, result: null, error: "This saved review could not be opened. Request a new AI review." });
    }
  };
  const requestReview = async () => {
    if (request.current.pending || disabled || !canRequest) return;
    if (!currentScope()) { setState({ pending: false, result: null, error: STALE }); return; }
    const snapshot = { ...live.current };
    const currentReview = buildOcrAiCurrentReview(snapshot.review, snapshot.items);
    const id = request.current.id + 1;
    const controller = new AbortController();
    request.current = { id, pending: true, controller };
    setState({ pending: true, result: null, error: "" });
    busyCallback.current?.(true);
    try {
      const { data } = await requestClient.post(`/api/purchasing/ocr-imports/${snapshot.importId}/ai-review`, {
        language: locale.startsWith("es") ? "es" : "en", current_review: currentReview,
      }, { timeout: 150000, signal: controller.signal, pilcoreExpectedUserId: ownerId,
        headers: { "X-Pilcore-Business-Unit-Id": snapshot.businessUnitId } });
      if (request.current.id !== id || controller.signal.aborted) return;
      if (!currentScope() || !live.current.documentAvailable || live.current.signature !== snapshot.signature || live.current.importId !== snapshot.importId || live.current.businessUnitId !== snapshot.businessUnitId || live.current.originalIdentity !== snapshot.originalIdentity) {
        setState({ pending: false, result: null, error: STALE }); return;
      }
      const proposal = validateOcrAiProposal(data?.proposal, currentReview.lines.length);
      setState({ pending: false, error: "", result: { proposal, baseline: currentReview, signature: snapshot.signature, canApply: true, reviewId: data.review_id } });
      setHistory((previous) => ({ ...previous, reviews: [{ review_id: data.review_id, created_at: data.created_at || new Date().toISOString(),
        proposal, current_review: currentReview, can_reapply: true }, ...previous.reviews.filter((entry) => entry.review_id !== data.review_id)] }));
    } catch (error) {
      if (request.current.id !== id || controller.signal.aborted) return;
      const message = error?.code === "ECONNABORTED" ? errors.OCR_AI_REVIEW_TIMEOUT : errors[error.response?.data?.code];
      setState({ pending: false, result: null, error: message || "Could not review this document with AI. Try again or continue reviewing manually." });
    } finally {
      if (request.current.id === id) {
        request.current.pending = false;
        busyCallback.current?.(false);
      }
    }
  };
  const stale = Boolean(state.result && state.result.signature !== signature);
  const apply = () => {
    if (!state.result?.canApply || disabled || !canRequest || request.current.pending) return;
    if (state.result.signature !== live.current.signature || !currentScope()) {
      setState((previous) => ({ ...previous, error: STALE })); return;
    }
    onApply({ ...applyOcrAiProposal(live.current.review, state.result.proposal, live.current), aiSummary: state.result.proposal.summary });
    setState({ pending: false, result: null, error: "" });
  };
  const result = state.result;
  const rows = result ? ocrAiComparisonRows(result.baseline, result.proposal) : [];
  const cell = (value, field) => {
    if (value === null || value === undefined || value === "") return "—";
    if (field === "Unit level") return t({ unit: "Main unit", subunit: "Subunit", subunit2: "Second subunit" }[value] || "Unknown");
    return String(value);
  };
  const sectionLabel = (section) => typeof section === "string" ? t(section) : section.expense
    ? t("Expense {number}", { number: section.expense }) : t("Line {number}", { number: section.line });

  return <section className="ocr-ai" aria-label={t("AI review")} aria-busy={state.pending}>
    <div className="ocr-ai-heading">
      <div><h3 className="ocrp-panelTitle">{t("AI review")}</h3><p className="ocrp-muted">{t("Compare the original document with this draft before applying any changes.")}</p></div>
      <button type="button" className="ocrp-btn ocrp-btn--info" disabled={disabled || state.pending || !canRequest} onClick={requestReview}>
        {state.pending ? t("Reading with AI...") : state.error ? t("Retry") : t("Review with AI")}
      </button>
    </div>
    {state.pending && <p role="status">{t("Reading the original document. Your draft is preserved.")}</p>}
    {(state.error || stale) && <p className="ocrp-alert ocrp-alert--warning" role="alert">{t(stale ? STALE : state.error)}</p>}
    <div className="ocr-ai-history">
      <button type="button" className="ocrp-btn" aria-expanded={history.open} onClick={() => {
        if (history.open) setHistory((previous) => ({ ...previous, open: false }));
        else if (history.loaded) setHistory((previous) => ({ ...previous, open: true }));
        else loadHistory();
      }}>{t("Saved AI reviews")}</button>
      {history.open && <div className="ocr-ai-history-body">
        {history.pending && <p role="status">{t("Loading saved reviews...")}</p>}
        {history.error && <p role="alert">{t(history.error)} <button type="button" className="ocrp-btn" onClick={loadHistory}>{t("Reload history")}</button></p>}
        {!history.pending && history.loaded && !history.reviews.length && <p>{t("No saved AI reviews yet.")}</p>}
        {history.reviews.map((entry) => <article key={entry.review_id} className="ocr-ai-history-entry">
          <div><strong>{entry.created_at && Number.isFinite(Date.parse(entry.created_at)) ? new Date(entry.created_at).toLocaleString(locale) : t("Saved review")}</strong>
            <p>{entry.proposal?.summary}</p></div>
          <button type="button" className="ocrp-btn" disabled={disabled || state.pending} onClick={() => previewSaved(entry)}>{t("View review")}</button>
        </article>)}
      </div>}
    </div>
    {result && <>
      <OcrAiReviewComments key={result.reviewId} summary={result.proposal.summary} comments={[
        ...result.proposal.warnings,
        t("Applying replaces the proposed lines and expenses. Removed lines are shown below. Item, unit and purchase-order links may need review."),
        ...(rows.some((row) => row.unknown && row.kind !== "removed") ? [t("Unknown header and matched-line values keep the current value. Unknown units require a manual selection. Review these fields after applying.")] : []),
        t("Document totals are for comparison; line prices and expenses determine the draft total."),
      ]} />
      {result.saved && <p className="ocr-ai-note">{t(result.canApply ? "Saved review. Compare it with the current draft before applying it again." : "This review is available for consultation only; it cannot be reapplied to this draft.")}</p>}
      <div className="ocr-ai-totals">{[["subtotal", "Subtotal"], ["tax", "Tax"], ["total", "Document total"]].map(([key, label]) => <div key={key}><span>{t(label)}</span><strong>{cell(result.proposal.totals?.[key])}</strong></div>)}</div>
      {rows.length ? <div className="ocr-ai-table-wrap"><table className="ocr-ai-table">
        <thead><tr>{["Change", "Field", "Current draft", "Proposal"].map((label) => <th key={label}>{t(label)}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={index} data-change={row.kind}>
          <th scope="row">{sectionLabel(row.section)}<span>{t({ added: "Added", removed: "Removed", changed: "Changed" }[row.kind])}</span></th>
          <td>{t(row.field === "Unit level" ? "Unit selection" : row.field)}</td>
          <td>{cell(row.before, row.field)}</td>
          <td>{row.kind === "removed" ? t("Removed") : row.unknown ? t("Unknown") : cell(row.after, row.field)}{row.reason && <small>{row.reason}</small>}</td>
        </tr>)}</tbody>
      </table></div> : <p>{t("No field changes were proposed.")}</p>}
      <div className="ocrp-actions ocr-ai-actions">
        <button type="button" className="ocrp-btn" onClick={() => setState({ pending: false, result: null, error: "" })}>{t("Close preview")}</button>
        <button type="button" className="ocrp-btn ocrp-btn--success" disabled={disabled || !canRequest || !result.canApply || stale || state.pending} onClick={apply}>{t(result.saved ? "Apply saved review" : "Apply proposal")}</button>
      </div>
    </>}
  </section>;
}
